import Head from 'next/head';
import TextInput from '../components/TextInput';
import Textarea from '../components/Textarea';
import Notification from '../components//Notification';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { sendMessage } from '../services/contact';

const Contact: React.FC = () => {
  const [error, setError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  const {
    register,
    formState: { errors },
    setValue,
    handleSubmit
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      message: ''
    }
  });

  const onSubmit = async (data: Record<string, string>) => {
    if (isSubmitting) {
      return;
    }

    const { firstName, lastName, email, phone, message } = data;

    try {
      setIsSubmitting(true);
      await sendMessage({ firstName, lastName, email, phone, message });
      setIsSubmitted(true);
    } catch (e) {
      setError('We were unable to send your message. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Head>
        <title>Contact | York Round Table | #DoMore</title>
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <div className="mx-auto w-full max-w-screen-xl">
        <article className="mx-auto w-full max-w-screen-lg">
          <h1 className="text-4xl text-rt-orange text-center mb-10">Contact</h1>
          <p className="text-xl text-white text-center mb-10">
            Call us on 0121 456 4567 <br />
            Text &quot;Join&quot; and your postcode to 60066
          </p>
        </article>
      </div>

      {isSubmitted ? (
        <div className="mx-auto w-full max-w-screen-md">
          <div className="w-full flex items-center justify-center text-center p-10 bg-black bg-opacity-20 rounded-xl">
            <div className="text-white flex flex-col text-center items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-20 w-20 opacity-40 mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h4 className="text-xl text-rt-orange mb-4">We&apos;ve got your message</h4>
              <p className="text-white">We&apos;ll get back to you as soon as we can.</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="mx-auto w-full max-w-screen-md">
          <form onSubmit={handleSubmit(onSubmit)}>
            {error ? (
              <div>
                <Notification variant="error">{error}</Notification>
              </div>
            ) : null}
            <div>
              <div className="grid grid-cols-2 gap-4 mb-2">
                <TextInput
                  type="text"
                  id="firstName"
                  name="firstName"
                  label="First name"
                  onChange={(evt) => setValue('firstName', evt.target.value)}
                  required
                  error={errors.firstName && 'First name is required'}
                  // @ts-ignore
                  register={register}
                />
                <TextInput
                  type="text"
                  id="lastName"
                  name="lastName"
                  label="Last name"
                  onChange={(evt) => setValue('lastName', evt.target.value)}
                  required
                  error={errors.lastName && 'Last name is required'}
                  // @ts-ignore
                  register={register}
                />
                <TextInput
                  type="email"
                  id="email"
                  name="email"
                  label="Email address"
                  onChange={(evt) => setValue('email', evt.target.value)}
                  required
                  error={errors.email && 'Email address is required'}
                  validation={{
                    pattern:
                      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                  }}
                  // @ts-ignore
                  register={register}
                />
                <TextInput
                  type="tel"
                  id="phone"
                  name="phone"
                  label="Telephone number"
                  onChange={(evt) => setValue('phone', evt.target.value)}
                  required
                  error={errors.phone && 'Telephone number is required'}
                  // @ts-ignore
                  register={register}
                />
                <div className="col-span-2">
                  <Textarea
                    id="message"
                    name="message"
                    label="Message"
                    onChange={(evt) => setValue('message', evt.target.value)}
                    required
                    error={errors.message && 'Enter a message to send'}
                    // @ts-ignore
                    register={register}
                  />
                </div>
              </div>
              <div className="">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-10 inline-flex items-center justify-center bg-rt-orange rounded-xl p-3 text-xl text-center text-white">
                  {isSubmitting ? (
                    <svg
                      className="animate-spin h-6 w-6 mr-2 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth={4}></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 mr-2 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  )}
                  Submit
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default Contact;
